// settings/HamburgerMenu/hooks/useVoiceSettings.ts
import { useCallback, useEffect, useState } from "react";
import { useAtom } from "jotai";
import {
  voiceSpeedAtom,
  voicePitchAtom,
  voiceIntonationAtom,
  currentSpeakerAtom,
  T2SEngineAtom,
} from "~/Atom";
import * as characterDB from "~/components/db/CharacterDBUtils";
import { extractInfo } from "~/TTS/SpeakersList";
import { getVRMName } from "~/vrm_utils/VRMUtils";
import { VRM } from "@pixiv/three-vrm";
import {
  getVoicevoxConfig,
  getOpenaiConfig,
  getAivisSpeechConfig,
} from "~/components/db/CharacterDBUtils";

const defaultSpeakerIds = {
  voicevox: 1,
  openai: 1,
  aivisSpeech: 1878365376, // コハク-normal
};

// デフォルト値の定数
const defaultSpeed = 1;
const defaultPitch = 0;
const defaultIntonation = 1;

/**
 * 音声設定を管理するカスタムフック
 * 現在のVRMモデルに基づいて音声設定を更新する
 */
export function useVoiceSettings(loadedVrm: VRM | null) {
  const [, setSpeed] = useAtom(voiceSpeedAtom);
  const [, setPitch] = useAtom(voicePitchAtom);
  const [, setIntonation] = useAtom(voiceIntonationAtom);
  const [, setCurrentSpeaker] = useAtom(currentSpeakerAtom);
  const [T2SEngine] = useAtom(T2SEngineAtom);
  const [isVoiceReady, setIsVoiceReady] = useState(false);

  /**
   * VOICEVOXエンジン用の設定を適用する
   * @param characterName キャラクター名
   */
  const applyVoicevoxSettings = useCallback(
    // eslint-disable-next-line complexity
    async (characterName: string) => {
      const voicevox = await getVoicevoxConfig(characterName);
      const voicevoxInfo = extractInfo(
        "VOICEVOX",
        voicevox?.speaker_id ?? defaultSpeakerIds.voicevox
      );

      setCurrentSpeaker({
        Id: voicevox?.speaker_id ?? defaultSpeakerIds.voicevox,
        Name: voicevoxInfo?.name ?? "",
        Style: voicevoxInfo?.styleName ?? "",
      });
      setSpeed(voicevox?.speed ?? defaultSpeed);
      setPitch(voicevox?.pitch ?? defaultPitch);
      setIntonation(voicevox?.intonation ?? defaultIntonation);
    },
    [setCurrentSpeaker, setSpeed, setPitch, setIntonation]
  );

  /**
   * OpenAIエンジン用の設定を適用する
   * @param characterName キャラクター名
   */
  const applyOpenAISettings = useCallback(
    async (characterName: string) => {
      const openai = await getOpenaiConfig(characterName);
      const openaiInfo = extractInfo("OpenAI", openai?.speaker_id ?? defaultSpeakerIds.openai);

      setCurrentSpeaker({
        Id: openai?.speaker_id ?? defaultSpeakerIds.openai,
        Name: openaiInfo?.styleName ?? "",
        Style: openaiInfo?.name ?? "",
      });
    },
    [setCurrentSpeaker]
  );

  /**
   * AivisSpeechエンジン用の設定を適用する
   * @param characterName キャラクター名
   */

  const applyAivisSpeechSettings = useCallback(
    // eslint-disable-next-line complexity
    async (characterName: string) => {
      const aivisspeech = await getAivisSpeechConfig(characterName);
      const aivisInfo = extractInfo(
        "AivisSpeech",
        aivisspeech?.speaker_id ?? defaultSpeakerIds.aivisSpeech
      );
      setCurrentSpeaker({
        Id: aivisspeech?.speaker_id ?? defaultSpeakerIds.aivisSpeech,
        Name: aivisInfo?.name ?? "",
        Style: aivisInfo?.styleName ?? "",
      });
      setSpeed(aivisspeech?.speed ?? defaultSpeed);
      setPitch(aivisspeech?.pitch ?? defaultPitch);
      setIntonation(aivisspeech?.intonation ?? defaultIntonation);
    },
    [setCurrentSpeaker, setSpeed, setPitch, setIntonation]
  );

  /**
   * 現在のVRMモデルに基づいてスライダー値を更新する
   */
  const updateSliderValue = useCallback(async () => {
    const cachedName = getVRMName(loadedVrm);
    if (cachedName === "") return;

    try {
      const res = await characterDB.selectByName(cachedName);
      if (!res) return;

      switch (T2SEngine) {
        case "VOICEVOX":
          await applyVoicevoxSettings(res.name);
          break;
        case "OpenAI":
          await applyOpenAISettings(res.name);
          break;
        case "AivisSpeech":
          await applyAivisSpeechSettings(res.name);
          break;
        default:
          console.error("Unknown T2SEngine:", T2SEngine);
      }
      setIsVoiceReady(true);
    } catch (e) {
      console.error(e);
    } finally {
      setIsVoiceReady(true);
    }
  }, [loadedVrm, T2SEngine, applyVoicevoxSettings, applyOpenAISettings, applyAivisSpeechSettings]);

  useEffect(() => {
    updateSliderValue();
  }, [loadedVrm, T2SEngine]);

  return { updateSliderValue, isVoiceReady };
}
